import type {
  ChatMessage,
  ChatRole,
  ChatStreamEvent,
} from '../../hooks/chat/types';

const EVENT_TYPES = {
  TextDelta: 'TEXT_DELTA',
  ToolCallDelta: 'TOOL_CALL_DELTA',
  ToolCallExecute: 'TOOL_CALL_EXECUTE',
  ToolCallResult: 'TOOL_CALL_RESULT',
  MessageComplete: 'MESSAGE_COMPLETE',
  Cancelled: 'CANCELLED',
  Done: 'DONE',
  Error: 'ERROR',
} as const satisfies Record<string, ChatStreamEvent['type']>;

type EventKind = keyof typeof EVENT_TYPES;

type EventFields<K extends EventKind> = Omit<
  Extract<ChatStreamEvent, {type: (typeof EVENT_TYPES)[K]}>,
  'type'
>;

type RawEventFields<K extends EventKind> = K extends 'MessageComplete'
  ? Omit<EventFields<K>, 'message'> & {
      message: Omit<ChatMessage, 'role'> & {role: string};
    }
  : EventFields<K>;

// Any schema's StreamEventInterface member: only the typename prefix differs.
export type RawStreamEvent = {
  [K in EventKind]: {
    __typename: `${string}ChatStreamEvent${K}`;
    type?: unknown;
  } & RawEventFields<K>;
}[EventKind];

const isEventKind = (kind: string): kind is EventKind =>
  Object.prototype.hasOwnProperty.call(EVENT_TYPES, kind);

export function mapStreamEvent({
  type: _type,
  ...event
}: RawStreamEvent): ChatStreamEvent | null {
  const kind = event.__typename.replace(/^.*ChatStreamEvent/, '');
  if (!isEventKind(kind)) {
    return null;
  }

  const type = EVENT_TYPES[kind];
  if (type === 'MESSAGE_COMPLETE') {
    const {message, ...rest} = event as RawEventFields<'MessageComplete'>;
    return {
      ...rest,
      type,
      message: {...message, role: message.role.toLowerCase() as ChatRole},
    };
  }

  return {...event, type} as ChatStreamEvent;
}
